import type { JssStyle } from 'jss';
import type { WebLegacyWidgetEntity } from '@web/types';
import {
    type BeforeAfterWidgetData,
    EHandleOrientation,
} from '@web/widgets/before-after/type/before-after-widget.type';

import { EHorizontalPosition } from '@common/enums';

const justifyStyles = {
    [EHorizontalPosition.L]: 'flex-start',
    [EHorizontalPosition.M]: 'center',
    [EHorizontalPosition.R]: 'flex-end',
};

function getImgWidgetWrapperStyles(
    { data }: WebLegacyWidgetEntity<BeforeAfterWidgetData>,
    isConstructor: boolean
): JssStyle {
    let horizontalAlignValue: string;

    if ((data as any).mobileHorizontalAlign) {
        horizontalAlignValue = justifyStyles[(data as any).mobileHorizontalAlign];
    } else {
        horizontalAlignValue = justifyStyles[data.horizontalAlign];
    }

    const mediaQueryKey = '@media (max-width: 992px)';
    const styles: any = {
        position: 'relative',
        display: 'flex',
        justifyContent: justifyStyles[data.horizontalAlign],
        [mediaQueryKey]: {
            justifyContent: horizontalAlignValue,
        },
        '&.mobileWidgetViewWrap': {
            justifyContent: horizontalAlignValue,
        },
    };

    return styles;
}

function getImgWrapperStyles({ data }: WebLegacyWidgetEntity<BeforeAfterWidgetData>, isSpecific?: boolean): JssStyle {
    const imgWrapper: JssStyle = {
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        maxWidth: data.maxWidth + 'px',
    };

    if (!data.beforeImage) {
        imgWrapper.backgroundColor = '#F8FDFE';
    }

    return imgWrapper;
}

function getAfterImageWrapperStyles(
    { data }: WebLegacyWidgetEntity<BeforeAfterWidgetData>,
    isConstructor?: boolean
): JssStyle {
    let pos: JssStyle = {};

    if (data.handleOrientation === EHandleOrientation.horizontal) {
        pos = {
            clipPath: `inset(${data.handlePosition}% 0 0 0)`,
        };
    } else {
        pos = {
            clipPath: `inset(0 0 0 ${data.handlePosition}%)`,
        };
    }

    if (!isConstructor) {
        pos = {};
    }

    const afterImageWrapper: JssStyle = {
        display: 'block',
        height: '100%',
        width: '100%',
        position: 'absolute',
        inset: 0,
        objectFit: 'cover',
        // ...pos,
    };

    if (!data.afterImage) {
        afterImageWrapper.backgroundColor = '#F8FDFE';
    }

    return afterImageWrapper;
}

function getAfterImageStyles({ data }: WebLegacyWidgetEntity<BeforeAfterWidgetData>): JssStyle {
    if (!data.afterImage) {
        return {
            top: '50%',
            left: '50%',
            position: 'absolute',
            transform: 'translate(-50%, -50%)',
        };
    }

    const afterImage: JssStyle = {
        display: 'block',
        height: '100%',
        width: '100%',
        objectFit: 'cover',
    };

    return afterImage;
}

function getSliderHandleStyles({ data }: WebLegacyWidgetEntity<BeforeAfterWidgetData>): JssStyle {
    let pos: JssStyle = {};

    if (data.handleOrientation === EHandleOrientation.horizontal) {
        pos = {
            top: data.handlePosition ? `${data.handlePosition}%` : '50%',
            left: '50%',
        };
    } else {
        pos = {
            left: data.handlePosition ? `${data.handlePosition}%` : '50%',
            top: '50%',
        };
    }

    const sliderHandle: JssStyle = {
        color: data.handleColor,
        // ...pos,
        '& .handle-icon': {
            height: '40px',
            width: '40px',
        },
        '&.horizontal': {
            '& .handle-icon': {
                transform: 'rotate(90deg)',
            },
        },
    };

    return sliderHandle;
}

function getSliderBorderStyles({ data }: WebLegacyWidgetEntity<BeforeAfterWidgetData>): JssStyle {
    let pos: JssStyle = {};

    if (data.handleOrientation === EHandleOrientation.horizontal) {
        pos = {
            transform: 'translate(0%, -50%)',
            top: data.handlePosition ? `${data.handlePosition}%` : '50%',
            left: 0,
            right: 0,
            width: '100%',
            height: '4px',
        };
    } else {
        pos = {
            transform: 'translate(-50%, 0%)',
            left: data.handlePosition ? `${data.handlePosition}%` : '50%',
            top: 0,
            bottom: 0,
            width: '4px',
            height: '100%',
        };
    }

    const sliderBorder: JssStyle = {
        position: 'absolute',
        backgroundColor: data.handleColor,
        // ...pos,
    };

    return sliderBorder;
}

function getImageStyles({ data }: WebLegacyWidgetEntity<BeforeAfterWidgetData>): JssStyle {
    if (!data.beforeImage) {
        return {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        };
    }

    const imageStyles: JssStyle = {
        display: 'block',
        height: '100%',
        width: '100%',
        position: 'absolute',
        inset: 0,
        objectFit: 'cover',
    };

    return imageStyles;
}

export function getStyles(widget: WebLegacyWidgetEntity<BeforeAfterWidgetData>, config: any): JssStyle {
    return {
        imgWidgetWrapper: getImgWidgetWrapperStyles(widget, config.isConstructor),
        imgWrapper: getImgWrapperStyles(widget, config.isSpecific),
        afterImageWrapper: getAfterImageWrapperStyles(widget, config.isConstructor),
        afterImage: getAfterImageStyles(widget),
        sliderHandle: getSliderHandleStyles(widget),
        sliderBorder: getSliderBorderStyles(widget),
        image: getImageStyles(widget),
    };
}
